import { React, useState } from "react";
import {
  DollarSign,
  Target,
  Users,
  TrendingUp,
  Phone,
  CheckCircle,
  PhoneCall,
  Mail,
  CalendarDays,
  UserPlus,
} from "lucide-react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import PhoneVerificationDrawer from "../../Leads/Modals/PhoneVerificationDrawer";

const Dashboard = () => {
  const revenueData = [
    { month: "Jan", revenue: 42000, target: 40000 },
    { month: "Feb", revenue: 48000, target: 47000 },
    { month: "Mar", revenue: 51000, target: 50000 },
    { month: "Apr", revenue: 55000, target: 53000 },
    { month: "May", revenue: 62000, target: 60000 },
    { month: "Jun", revenue: 68000, target: 65000 },
  ];

  const pieData = [
    { name: "Prospecting", value: 31, color: "#3B82F6" },
    { name: "Qualified", value: 23, color: "#10B981" },
    { name: "Proposal", value: 16, color: "#F59E0B" },
    { name: "Negotiation", value: 10, color: "#8B5CF6" },
    { name: "Closed Won", value: 19, color: "#06B6D4" },
  ];

  const statusStyles = {
    Hot: "bg-red-100 text-red-600",
    Warm: "bg-yellow-100 text-yellow-600",
    Cold: "bg-blue-100 text-blue-600",
  };

  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="p-6 pt-1  min-h-screen">
        {/* HEADER */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              Dashboard Overview
            </h1>
            <p className="text-sm text-gray-500">
              Welcome back! Here's what's happening with your sales today.
            </p>
          </div>

          {/* ADD LEAD BUTTON */}
          <button
            onClick={() => setOpen(true)}
            className=" flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-medium shadow-sm transition duration-200 ease-out transform-gpu hover:bg-primary hover:shadow-md hover:shadow-primary/30 hover:scale-[1.02]"
          >
            + Add Lead
          </button>
        </div>

        {/*  CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card title="Total Revenue" value="$324K" icon={<DollarSign />} />
          <Card title="Active Deals" value="77" icon={<Target />} />
          <Card title="New Leads" value="142" icon={<Users />} />
          <Card title="Conversion Rate" value="68%" icon={<TrendingUp />} red />
        </div>

        {/* CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* REVENUE CHART */}
          <div className="bg-white p-5 rounded-xl shadow col-span-2">
            <h2 className="text-lg font-semibold mb-4">Revenue Overview</h2>

            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.35} />
<stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />

                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0F172A",
                    border: "none",
                    borderRadius: "8px",
                    color: "#fff",
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0EA5E9"
                  strokeWidth={3}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* PIE CHART */}
          <div className="bg-white p-5 rounded-xl shadow">
            <h2 className="text-lg font-semibold mb-4">Deals Pipeline</h2>

            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) =>
                    ` ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>

                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TABLE + ACTIVITY */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* TABLE */}
          <div className="bg-white rounded-xl shadow col-span-2 overflow-hidden">
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold">Recent Leads</h2>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-6 py-3 text-left">Contact</th>
                  <th className="px-6 py-3 text-left">Company</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Deal Value</th>
                  <th className="px-6 py-3 text-left">Contact Info</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {[
                  {
                    name: "Sarah Johnson",
                    initials: "SJ",
                    company: "Tech Corp",
                    status: "Hot",
                    value: "$45,000",
                  },
                  {
                    name: "Michael Chen",
                    initials: "MC",
                    company: "Innovation Labs",
                    status: "Warm",
                    value: "$32,000",
                  },
                  {
                    name: "Emily Davis",
                    initials: "ED",
                    company: "Digital Solutions",
                    status: "Hot",
                    value: "$58,000",
                  },
                  {
                    name: "James Wilson",
                    initials: "JW",
                    company: "Future Systems",
                    status: "Cold",
                    value: "$15,000",
                  },
                  {
                    name: "Lisa Anderson",
                    initials: "LA",
                    company: "Smart Tech",
                    status: "Warm",
                    value: "$41,000",
                  },
                ].map((lead, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {/* CONTACT */}
                    <td className="px-6 py-4 flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold">
                        {lead.initials}
                      </div>
                      <span className="font-medium text-gray-800">
                        {lead.name}
                      </span>
                    </td>

                    {/* COMPANY */}
                    <td className="px-6 py-4 text-gray-600">{lead.company}</td>

                    {/* STATUS */}
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          statusStyles[lead.status]
                        }`}
                      >
                        {lead.status}
                      </span>
                    </td>

                    {/* DEAL VALUE */}
                    <td className="px-6 py-4 font-medium">{lead.value}</td>

                    {/* CONTACT INFO */}
                    <td className="px-6 py-4 flex gap-4 text-gray-500">
                      <Mail
                        size={16}
                        className="cursor-pointer hover:text-blue-500"
                      />
                      <Phone
                        size={16}
                        className="cursor-pointer hover:text-green-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ACTIVITY */}
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>

            <div className="space-y-5 text-sm">
              <ActivityItem
                icon={<CheckCircle size={16} />}
                bg="bg-green-100 text-green-600"
                text="Deal closed with Tech Corp"
                time="2 hours ago"
              />
              <ActivityItem
                icon={<PhoneCall size={16} />}
                bg="bg-blue-100 text-blue-600"
                text="Called Michael Chen - Follow up needed"
                time="4 hours ago"
              />
              <ActivityItem
                icon={<Mail size={16} />}
                bg="bg-purple-100 text-purple-600"
                text="Sent proposal to Digital Solutions"
                time="5 hours ago"
              />
              <ActivityItem
                icon={<CalendarDays size={16} />}
                bg="bg-orange-100 text-orange-600"
                text="Meeting scheduled with Future Systems"
                time="1 day ago"
              />
              <ActivityItem
                icon={<UserPlus size={16} />}
                bg="bg-pink-100 text-pink-600"
                text="New lead added - Lisa Anderson"
                time="1 day ago"
              />
            </div>
          </div>
        </div>
      </div>
      <PhoneVerificationDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
};

/* COMPONENTS */

const Card = ({ title, value, icon, red }) => (
  <div className="bg-white rounded-xl shadow p-5 flex justify-between items-center">
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <h2 className="text-2xl font-bold">{value}</h2>
    </div>
    <div
      className={`p-3 rounded-lg ${
        red ? "bg-red-100 text-red-500" : "bg-green-100 text-green-600"
      }`}
    >
      {icon}
    </div>
  </div>
);

const Activity = ({ color, text, time }) => (
  <div className="flex gap-3">
    <div className={`bg-${color}-100 p-2 rounded-full`}>✓</div>
    <div>
      <p>{text}</p>
      <span className="text-gray-400 text-xs">{time}</span>
    </div>
  </div>
);

const ActivityItem = ({ icon, bg, text, time }) => (
  <div className="flex items-start gap-3">
    <div className={`p-2 rounded-lg ${bg}`}>{icon}</div>
    <div>
      <p className="text-gray-800">{text}</p>
      <span className="text-gray-400 text-xs">{time}</span>
    </div>
  </div>
);

export default Dashboard;
